import { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

/**
 * Labelled password field with a show/hide toggle.
 *
 * Public interface: standard input props (id, label, value, onChange,
 * placeholder, autoComplete, minLength, ...). Hides the visibility state, the
 * eye toggle button, and its a11y labelling. Uses the canonical `.form-input`
 * styling so it matches the rest of the form kit.
 *
 * Props:
 *   id        — ties the <label> to the <input> (required for a11y)
 *   label     — label text; omit to skip the label element
 *   error     — error string; renders below the field and applies the
 *               canonical `.form-input--error` styling when truthy
 *   className — extra class(es) on the wrapping .form-group
 *
 * All other props pass through to the <input>.
 */
export default function PasswordField({ id, label, error, className = '', ...props }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={['form-group', className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <div className="password-field-row">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className={['form-input', error ? 'form-input--error' : ''].filter(Boolean).join(' ')}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          {...props}
        />
        <button
          type="button"
          className="btn btn-secondary password-field-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          title={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <FiEye size={18} /> : <FiEyeOff size={18} />}
        </button>
      </div>
      {error && (
        <p id={`${id}-error`} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
